import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../lib/utils';

interface CopyButtonProps {
  /** The text to copy to the clipboard. */
  value?: string | null;
  /** Label used in the success toast and tooltip, e.g. "Asset ID". */
  label?: string;
  className?: string;
}

/**
 * Small icon button that copies a value to the clipboard and briefly shows a tick.
 * No-ops (and hides) when there is nothing to copy, and falls back gracefully if
 * the Clipboard API is unavailable.
 */
export function CopyButton({ value, label = 'Value', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const text = (value ?? '').toString().trim();
  if (!text) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      className={cn(
        'inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer align-middle',
        className
      )}
    >
      {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
    </button>
  );
}
